/**
 * Apply-credit confirm dialog (gdn-2950): move an over-collection or the
 * account's unapplied credit onto an encounter that owes — or, for the
 * account's credit, onto a charge on the account (gdn-4321).
 *
 * The applied amount is always min(the option's cap, its remaining due) —
 * the server enforces the same caps, so the number shown is the number
 * posted. A visit and an account charge go through different verbs with
 * different caps; creditOptionsOf carries each option's own.
 * Mount per open (parent renders conditionally): the idempotency key is
 * generated once per mount, so a retry after an error reuses it and the
 * server dedups. Both verbs key on it, and both refuse the key when it
 * comes back with a different target, so switching targets after a lost
 * response can't apply the credit twice.
 * The dialog records each confirm itself, so every caller (the balance
 * table, and the settle sheet's Apply credit modal) records it.
 */

import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';

import {
  type ChargeCreditAllocationRequest,
  type CreditAllocationRequest,
  useChargeCreditAllocationMutation,
  useCreditAllocationMutation,
} from '../../store/v2-api';

import {
  appliedCentsOf,
  type AppliedCredit,
  type CreditOption,
  creditOptionsOf,
} from './creditOptions';
import {
  type AccountChargeTarget,
  type CreditSource,
  type CreditTarget,
  formatCents,
} from './rows';
import { serverReasonOf } from './serverReason';

export type { AppliedCredit } from './creditOptions';

export interface ApplyCreditDialogProps {
  patientId: string;
  source: CreditSource;
  targets: CreditTarget[];
  /** Charges on the account the account's credit can pay (gdn-4321). */
  accountCharges?: AccountChargeTarget[];
  /** 'standalone' owns its Dialog (the panel mounts it inline);
   * 'hosted' renders bare content because GlobalModalManager already
   * supplied the Dialog. */
  surface?: 'standalone' | 'hosted';
  onClose: () => void;
  /** Fired after the server accepts the allocation. */
  onApplied?: (applied: AppliedCredit) => void;
}

export const ApplyCreditDialog: React.FC<ApplyCreditDialogProps> = ({
  patientId,
  source,
  surface = 'standalone',
  targets,
  accountCharges = [],
  onClose,
  onApplied,
}) => {
  const tracking = useEventTracking();
  // One idempotency key per dialog open — stable across retries.
  const [allocationId] = useState(() => crypto.randomUUID());
  const options = useMemo(
    () => creditOptionsOf(source, targets, accountCharges),
    [source, targets, accountCharges],
  );
  const [optionKey, setOptionKey] = useState<string | null>(() => {
    const selectable = options.filter(option => !option.blockedReason);
    return selectable.length === 1 ? selectable[0].key : null;
  });
  const [errorText, setErrorText] = useState<string | null>(null);
  const [postAllocation, { isLoading: isPostingVisit }] = useCreditAllocationMutation();
  const [postChargeCredit, { isLoading: isPostingCharge }] =
    useChargeCreditAllocationMutation();
  const isLoading = isPostingVisit || isPostingCharge;

  // The choice counts only while it can still take credit. A refresh while
  // the dialog is open can put the chosen charge on an invoice; its radio
  // then disables, and Apply credit must not stay live beside it.
  const option = useMemo(
    () =>
      options.find(candidate => candidate.key === optionKey && !candidate.blockedReason) ??
      null,
    [options, optionKey],
  );
  const appliedCents = option ? appliedCentsOf(option) : null;

  const applyToVisit = async (visit: CreditOption, amountCents: number) => {
    const request: CreditAllocationRequest = {
      allocation_id: allocationId,
      target_encounter_id: visit.id,
      source_encounter_id: source.sourceEncounterId,
      amount_cents: amountCents,
    };
    tracking.billing.patientCreditAllocationSubmitted({
      patient_id: patientId,
      amount_cents: amountCents,
      target_encounter_id: visit.id,
      source: source.kind,
    });
    await postAllocation({ patientId, creditAllocationRequest: request }).unwrap();
    onApplied?.({ target: 'visit', request });
  };

  const applyToCharge = async (charge: CreditOption, amountCents: number) => {
    const request: ChargeCreditAllocationRequest = {
      allocation_id: allocationId,
      amount_cents: amountCents,
      charge_line_id: charge.id,
    };
    tracking.billing.patientChargeCreditAllocationSubmitted({
      patient_id: patientId,
      charge_line_id: charge.id,
      amount_cents: amountCents,
    });
    const result = await postChargeCredit({
      patientId,
      chargeCreditAllocationRequest: request,
    }).unwrap();
    onApplied?.({ target: 'account_charge', request, result });
  };

  const handleConfirm = async () => {
    if (!option || !appliedCents) return;
    setErrorText(null);
    try {
      if (option.kind === 'visit') await applyToVisit(option, appliedCents);
      else await applyToCharge(option, appliedCents);
    } catch (error) {
      setErrorText(
        serverReasonOf(error) ??
          'Could not apply the credit. The balance may have changed — close and try again.',
      );
    }
  };

  const hasAccountCharges = options.some(candidate => candidate.kind === 'account_charge');
  const visitOptions = options.filter(candidate => candidate.kind === 'visit');
  const chargeOptions = options.filter(candidate => candidate.kind === 'account_charge');
  const title = hasAccountCharges ? 'Apply credit' : 'Apply credit to a visit';

  // Never an empty list (gdn-4176). With no visit to choose, the picker
  // rendered nothing and the button could never enable — the screen
  // Island Life staff quoted back to us (IL01). The settle sheet's prompt
  // no longer offers this dialog without a target, but the banked-credit
  // snackbar and any future caller can still open it, so it explains
  // itself here rather than trusting every caller to check.
  const noTargetBody = (
    <>
      <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>
        {title}
      </DialogTitle>
      <DialogContent>
        {/* "Confirmed": estimates waiting on the ERA and held visits can
            look owed, and neither is a target. Since gdn-4321 a charge on
            the account can take the account's credit, so the sentence
            names both kinds of target. */}
        <Alert severity="info">
          No visit or account charge has a confirmed amount still owed, so there is nothing
          to apply this {formatCents(Math.max(source.creditCents, source.chargeCreditCents ?? 0))}{' '}
          credit to yet. It stays on the account.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>
    </>
  );

  const showsVisitHoldback = visitOptions.length > 0 && source.heldBackCents > 0;
  const pickerBody = (
    <>
      <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>
        {title}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: showsVisitHoldback ? 0.5 : 1.5, color: 'text.secondary' }}>
          {source.label} holds a{' '}
          {formatCents(source.creditCents + source.heldBackCents)} credit.
          {hasAccountCharges ? ' Choose what to apply it to:' : ' Choose the visit to apply it to:'}
        </Typography>
        {showsVisitHoldback && (
          // The row shows the gross credit; a visit can take only what the
          // account's own open charges leave. Say why the two numbers
          // differ so they never look like a disagreement.
          <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
            A visit can take up to {formatCents(source.creditCents)}. The rest covers
            the account&apos;s open charges.
          </Typography>
        )}
        <RadioGroup
          aria-label={hasAccountCharges ? 'What to apply the credit to' : 'Visit to apply the credit to'}
          value={option?.key ?? ''}
          onChange={event => {
            setOptionKey(event.target.value);
            setErrorText(null);
          }}
        >
          {hasAccountCharges && visitOptions.length > 0 && <OptionHeading>Visits</OptionHeading>}
          {visitOptions.map(candidate => (
            <CreditOptionRow key={candidate.key} option={candidate} />
          ))}
          {hasAccountCharges && <OptionHeading>Charges on the account</OptionHeading>}
          {chargeOptions.map(candidate => (
            <CreditOptionRow key={candidate.key} option={candidate} />
          ))}
        </RadioGroup>
        {option && appliedCents !== null && (
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
            Applies {formatCents(appliedCents)}.{' '}
            <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
              {remainderNote(option, appliedCents)}
            </Typography>
          </Typography>
        )}
        {errorText && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {errorText}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!option || isLoading}
          onClick={() => void handleConfirm()}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {isLoading ? 'Applying…' : 'Apply credit'}
        </Button>
      </DialogActions>
    </>
  );

  const body = options.length === 0 ? noTargetBody : pickerBody;

  // Hosted inside GlobalModalManager, which already supplies the
  // Dialog, its backdrop and its transition — same reasoning as
  // SheetSurface's 'dialog' variant. Rendering our own Dialog there
  // nests two, which doubles the backdrop and puts a second focus
  // trap and Escape handler in front of the first.
  if (surface === 'hosted') return body;

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      {body}
    </Dialog>
  );
};

const OptionHeading = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="caption"
    sx={{ color: 'text.secondary', fontWeight: 700, mt: 0.5, mb: 0.5 }}
  >
    {children}
  </Typography>
);

/** One visit or account charge in the picker. A charge the credit can't
 * pay yet (on an open invoice) is listed, disabled, with the reason. */
const CreditOptionRow = ({ option }: { option: CreditOption }) => {
  const owes = `owes ${formatCents(option.remainingDueCents)}`;
  const detail = option.blockedReason ?? `${option.date ?? 'No date'} · ${owes}`;
  return (
    <FormControlLabel
      value={option.key}
      disabled={Boolean(option.blockedReason)}
      control={<Radio size="small" />}
      label={
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {option.label}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {detail}
          </Typography>
        </Box>
      }
      aria-label={`${option.label}, ${option.blockedReason ?? owes}`}
      sx={{ alignItems: 'flex-start', mb: 0.75 }}
    />
  );
};

const remainderNote = (option: CreditOption, appliedCents: number): string => {
  const creditLeft = option.capCents - appliedCents;
  const stillOwes = option.remainingDueCents - appliedCents;
  const noun = option.kind === 'visit' ? 'the visit' : 'the charge';
  const settles = option.kind === 'visit' ? 'Settles the visit' : 'Pays the charge';
  if (creditLeft > 0) {
    return `${settles}; ${formatCents(creditLeft)} of the credit stays on the account.`;
  }
  if (stillOwes > 0) {
    return `Uses the whole credit; ${noun} still owes ${formatCents(stillOwes)}.`;
  }
  return `${settles} and uses the whole credit.`;
};
